/**
 * Page d'administration : gestion des practices.
 */

import React, { useEffect, useState } from "react";
import { getPractices, createPractice, updatePractice, deletePractice, getSquads, getUsers } from "../api/adminApi";
import ConfirmDialog from "../components/ConfirmDialog";
import AdminNav from "../components/AdminNav";
import "./PageStyles.css";

export default function AdminPracticeListPage() {
  const [practices, setPractices] = useState([]);
  const [squads, setSquads] = useState([]);
  const [users, setUsers] = useState([]);
  const [deleteId, setDeleteId] = useState(null);
  const [editPractice, setEditPractice] = useState(null); // null | { id?, name, squad_ids, lead_ids }
  const [success, setSuccess] = useState("");

  useEffect(() => {
    loadData();
  }, []);

  async function loadData() {
    const [practicesData, squadsData, usersData] = await Promise.all([
      getPractices(), getSquads(), getUsers(),
    ]);
    setPractices(practicesData);
    setSquads(squadsData);
    setUsers(usersData);
  }

  async function handleDelete() {
    await deletePractice(deleteId);
    setDeleteId(null);
    setSuccess("Practice supprimée.");
    setTimeout(() => setSuccess(""), 3000);
    loadData();
  }

  async function handleSave() {
    if (!editPractice.name.trim()) return;
    if (editPractice.id) {
      await updatePractice(editPractice.id, {
        name: editPractice.name,
        squad_ids: editPractice.squad_ids,
        lead_ids: editPractice.lead_ids,
      });
    } else {
      await createPractice({
        name: editPractice.name,
        squad_ids: editPractice.squad_ids,
        lead_ids: editPractice.lead_ids,
      });
    }
    setEditPractice(null);
    setSuccess(editPractice.id ? "Practice mise à jour." : "Practice créée.");
    setTimeout(() => setSuccess(""), 3000);
    loadData();
  }

  function openCreate() {
    setEditPractice({ name: "", squad_ids: [], lead_ids: [] });
  }

  function openEdit(practice) {
    setEditPractice({
      id: practice.id,
      name: practice.name,
      squad_ids: [...practice.squad_ids],
      lead_ids: [...practice.lead_ids],
    });
  }

  function toggleSquad(squadId) {
    if (!editPractice) return;
    const ids = editPractice.squad_ids.includes(squadId)
      ? editPractice.squad_ids.filter((id) => id !== squadId)
      : [...editPractice.squad_ids, squadId];
    setEditPractice({ ...editPractice, squad_ids: ids });
  }

  function toggleLead(userId) {
    if (!editPractice) return;
    const ids = editPractice.lead_ids.includes(userId)
      ? editPractice.lead_ids.filter((id) => id !== userId)
      : [...editPractice.lead_ids, userId];
    setEditPractice({ ...editPractice, lead_ids: ids });
  }

  function getUserName(userId) {
    const u = users.find((u) => u.id === userId);
    return u ? `${u.first_name} ${u.last_name}` : `#${userId}`;
  }

  function getSquadName(squadId) {
    const s = squads.find((s) => s.id === squadId);
    return s ? s.name : `#${squadId}`;
  }

  return (
    <div className="page">
      <AdminNav />
      <div className="page-header">
        <h2>Practices</h2>
        <button className="btn btn-primary" onClick={openCreate}>
          Nouvelle practice
        </button>
      </div>
      {success && <div className="success-message">{success}</div>}

      {practices.length === 0 ? (
        <p style={{ color: "#64748b", textAlign: "center", padding: "2rem" }}>
          Aucune practice configurée.
        </p>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
          {practices.map((practice) => (
            <div key={practice.id} className="form-card" style={{ margin: 0 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "0.5rem" }}>
                <h3 style={{ margin: 0, fontSize: "1.1rem" }}>{practice.name}</h3>
                <div style={{ display: "flex", gap: "0.5rem" }}>
                  <button className="btn btn-small" onClick={() => openEdit(practice)}>
                    Modifier
                  </button>
                  <button className="btn btn-small btn-danger" onClick={() => setDeleteId(practice.id)}>
                    Supprimer
                  </button>
                </div>
              </div>
              <div style={{ fontSize: "0.9rem", color: "#475569" }}>
                <div style={{ marginBottom: "0.25rem" }}>
                  <strong>Leads :</strong>{" "}
                  {practice.lead_ids.length > 0
                    ? practice.lead_ids.map((id) => getUserName(id)).join(", ")
                    : "—"}
                </div>
                <div>
                  <strong>Squads :</strong>{" "}
                  {practice.squad_ids.length > 0
                    ? practice.squad_ids.map((id) => getSquadName(id)).join(", ")
                    : "—"}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {deleteId && (
        <ConfirmDialog
          message="Supprimer cette practice ?"
          onConfirm={handleDelete}
          onCancel={() => setDeleteId(null)}
        />
      )}

      {editPractice && (
        <div className="confirm-overlay">
          <div className="confirm-box" style={{ maxWidth: "550px" }}>
            <h3 style={{ marginTop: 0 }}>
              {editPractice.id ? "Modifier la practice" : "Nouvelle practice"}
            </h3>
            <div className="form-group">
              <label>Nom de la practice</label>
              <input
                type="text"
                value={editPractice.name}
                onChange={(e) => setEditPractice({ ...editPractice, name: e.target.value })}
                autoFocus
              />
            </div>
            <div style={{ display: "flex", gap: "1.5rem" }}>
              <div style={{ flex: 1 }}>
                <p style={{ fontWeight: 600, fontSize: "0.9rem", marginBottom: "0.5rem" }}>
                  Leads
                </p>
                <div style={{ maxHeight: "250px", overflow: "auto" }}>
                  {users.map((u) => (
                    <label key={u.id} style={{ display: "flex", alignItems: "center", gap: "0.5rem", padding: "0.3rem 0", fontSize: "0.9rem", cursor: "pointer" }}>
                      <input
                        type="checkbox"
                        checked={editPractice.lead_ids.includes(u.id)}
                        onChange={() => toggleLead(u.id)}
                      />
                      {u.first_name} {u.last_name}
                    </label>
                  ))}
                </div>
              </div>
              <div style={{ flex: 1 }}>
                <p style={{ fontWeight: 600, fontSize: "0.9rem", marginBottom: "0.5rem" }}>
                  Squads
                </p>
                <div style={{ maxHeight: "250px", overflow: "auto" }}>
                  {squads.map((s) => (
                    <label key={s.id} style={{ display: "flex", alignItems: "center", gap: "0.5rem", padding: "0.3rem 0", fontSize: "0.9rem", cursor: "pointer" }}>
                      <input
                        type="checkbox"
                        checked={editPractice.squad_ids.includes(s.id)}
                        onChange={() => toggleSquad(s.id)}
                      />
                      {s.name}
                    </label>
                  ))}
                </div>
              </div>
            </div>
            <div className="confirm-actions" style={{ marginTop: "1rem" }}>
              <button className="btn btn-secondary" onClick={() => setEditPractice(null)}>
                Annuler
              </button>
              <button className="btn btn-primary" onClick={handleSave}>
                {editPractice.id ? "Enregistrer" : "Créer"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
